import React, { useState, useEffect } from 'react';
import messageService from '../../services/messageService';
import useStore from '../../store/useStore';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const ComposeMessageModal = ({ onClose, onSent }) => {
  const { user, toast } = useStore();
  const [loading, setLoading] = useState(false);
  const [fetchingRecipients, setFetchingRecipients] = useState(true);
  const [error, setError] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [audience, setAudience] = useState(
    user?.role === 'SuperAdmin'
      ? 'company-admins'
      : user?.role === 'CompanyAdmin'
        ? 'superadmins'
        : 'team-members'
  );
  const [formData, setFormData] = useState({
    recipientIds: [],
    subject: '',
    message: ''
  });

  const canChooseAudience = user?.role === 'SuperAdmin' || user?.role === 'CompanyAdmin';
  const isBulkSend = sendToAll || formData.recipientIds.length > 1;

  // Fetch available recipients
  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        setFetchingRecipients(true);
        const res = await messageService.getAvailableRecipients(audience);
        setRecipients(res.data.data || []);
      } catch (error) {
        console.error('Error fetching recipients:', error);
        setError('Failed to load recipients');
      } finally {
        setFetchingRecipients(false);
      }
    };

    fetchRecipients();
  }, [audience]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'recipientIds') {
      // Keep string values so React multiple select stays in sync.
      const selectedOptions = Array.from(e.target.selectedOptions, option => String(option.value)).filter(Boolean);
      setFormData(prev => ({
        ...prev,
        recipientIds: selectedOptions
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError(''); // Clear error when user types
  };

  const handleAudienceChange = (e) => {
    const nextAudience = e.target.value;
    setAudience(nextAudience);
    setSendToAll(false);
    setFormData((prev) => ({
      ...prev,
      recipientIds: []
    }));
    setError('');
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.subject.trim() || !formData.message.trim()) {
      setError('Please fill in all fields');
      return;
    }

    // Check recipients
    if (!sendToAll && formData.recipientIds.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        audience
      };

      let response;

      const numericRecipientIds = formData.recipientIds
        .map((id) => Number(id))
        .filter(Number.isFinite);

      if (isBulkSend) {
        response = await messageService.sendBulkMessage({
          ...payload,
          recipientIds: numericRecipientIds,
          sendToAll
        });
      } else {
        response = await messageService.sendMessage({
          ...payload,
          recipientId: numericRecipientIds[0]
        });
      }

      toast(response?.data?.message || 'Message sent successfully', 'success');

      // Reset form
      setFormData({
        recipientIds: [],
        subject: '',
        message: ''
      });
      setSendToAll(false);

      // Show success and close
      onSent();
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send message';
      setError(errorMessage);
      toast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const getRecipientTypeLabel = () => {
    if (user?.role === 'SuperAdmin') {
      return audience === 'all-users' ? 'All Users' : 'Company Admins';
    } else if (user?.role === 'CompanyAdmin') {
      return audience === 'superadmins' ? 'SuperAdmins' : 'Team Members';
    }
    return 'Users';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-lg shadow-xl max-w-md w-full max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Compose Message</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition"
          >
            <CloseIcon className="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 flex items-start gap-3">
              <ErrorIcon className="text-red-600 flex-shrink-0 mt-0.5" fontSize="small" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Send to All Option */}
          {recipients.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 flex items-center gap-3">
              <input
                type="checkbox"
                id="sendToAll"
                checked={sendToAll}
                onChange={(e) => {
                  setSendToAll(e.target.checked);
                  if (e.target.checked) {
                    setFormData(prev => ({ ...prev, recipientIds: [] }));
                  }
                }}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="sendToAll" className="text-sm text-blue-900 cursor-pointer">
                Send to all {getRecipientTypeLabel().toLowerCase()}
              </label>
            </div>
          )}

          {canChooseAudience && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audience
              </label>
              <select
                name="audience"
                value={audience}
                onChange={handleAudienceChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {user?.role === 'SuperAdmin' ? (
                  <>
                    <option value="company-admins">Company Admins</option>
                    <option value="all-users">All Users</option>
                  </>
                ) : (
                  <>
                    <option value="superadmins">SuperAdmins</option>
                    <option value="team-members">Team Members</option>
                  </>
                )}
              </select>
            </div>
          )}

          {/* Recipient Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {sendToAll ? `Sending to all ${getRecipientTypeLabel().toLowerCase()}` : `Select ${getRecipientTypeLabel()}`}
            </label>
            {fetchingRecipients ? (
              <div className="bg-gray-100 rounded px-3 py-2 text-sm text-gray-600">
                Loading recipients...
              </div>
            ) : recipients.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2 text-sm text-yellow-800">
                No available recipients
              </div>
            ) : sendToAll ? (
              <div className="bg-blue-100 border border-blue-300 rounded px-3 py-2 text-sm text-blue-900">
                Message will be sent to {recipients.length} recipient(s)
              </div>
            ) : (
              <select
                name="recipientIds"
                value={formData.recipientIds}
                onChange={handleChange}
                disabled={sendToAll}
                multiple
                size={Math.min(5, recipients.length)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
                required
              >
                <option value="" disabled>Select one or more recipients (hold Ctrl to select multiple)...</option>
                {recipients.map(recipient => (
                  <option key={recipient.id} value={String(recipient.id)}>
                    {recipient.firstName} {recipient.lastName}
                    {recipient.role ? ` - ${recipient.role}` : ''}
                    {recipient.email ? ` (${recipient.email})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Message subject"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Type your message..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              required
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || fetchingRecipients || recipients.length === 0}
            className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </>
            ) : (
              <>
                <SendIcon fontSize="small" />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComposeMessageModal;
